import React, { FC, useEffect, useState }     from 'react';
import { v4 as uuid }                         from 'uuid';
import { Button, Form, Icon, Message, Modal } from 'semantic-ui-react';
import { useCentralCalls }                    from '@src/rest/CentralCalls';
import { FlexAlign, FlexJustify }             from '@src/types';
import { Flexbox }                            from '@src/views/containers';
import SOWTabMenu                             from '@src/views/pages/consultant-dash/SOWTabMenu';
import DiscoveryWorkflow                      from '@src/views/pages/projects/discovery-questions/DiscoveryWorkflow';
import DocumentVersionMini                    from './DocumentVersionMini';
import type { ClientOutline, EditSowPayload, SOWOutline } from './ClientOutlineTypes';

interface ClientContentProps {
    clientId?: string;
    clientName?: string;
    onOpenAddSowDialog?: (
        clientId: string,
        clientName: string,
        onSaved?: () => void,
        editSow?: EditSowPayload
    ) => void;
    onOpenAddOfferingDialog?: ( clientId: string, sowId: string, sowName: string, onSaved?: () => void ) => void;
}

interface MsaRow {
    Id: string;
    DocumentItemId?: string;
    DocumentTypeId?: string;
    ClientOfferingId?: string;
    SOWId?: string;
}

interface DocumentItemRow {
    Id: string;
}

interface ClientRow {
    Id: string;
    Name?: string;
    ClientCode?: string;
}

const MSA_TABLE = 'MSAs';
const DOCUMENT_ITEMS_TABLE = 'DocumentItems';
const MSA_DOCUMENT_TYPE_ID = '23252eef-fafe-4a30-a99d-e52e05a5e5bb';
const CDA_DOCUMENT_TYPE_ID = 'd311d996-0f7d-419b-809a-255353935ba8';

const getCaseInsensitiveValue = <T = unknown>( obj: unknown, key: string ): T | undefined => {
    if( !obj || typeof obj !== 'object' ) return undefined;
    const record = obj as Record<string, unknown>;
    const foundKey = Object.keys( record ).find( k => k.toLowerCase() === key.toLowerCase() );
    return foundKey ? ( record[ foundKey ] as T ) : undefined;
};

const asOptionalString = ( value: unknown ): string | undefined => {
    if( value == null ) return undefined;
    const normalized = `${ value }`.trim();
    return normalized || undefined;
};

const asOptionalNumber = ( value: unknown ): number | undefined => {
    if( value == null || value === '' ) return undefined;
    const parsed = typeof value === 'number' ? value : Number( value );
    return Number.isFinite( parsed ) ? parsed : undefined;
};

const asOptionalBoolean = ( value: unknown ): boolean | undefined => {
    if( value == null || value === '' ) return undefined;
    if( typeof value === 'boolean' ) return value;
    if( typeof value === 'number' ) return value !== 0;
    const normalized = `${ value }`.trim().toLowerCase();
    if( normalized === 'true' || normalized === '1' ) return true;
    if( normalized === 'false' || normalized === '0' ) return false;
    return undefined;
};

const normalizeMsaRow = ( row: unknown ): MsaRow | null => {
    const id = asOptionalString( getCaseInsensitiveValue( row, 'Id' ) );
    if( !id ) return null;

    return {
        Id: id,
        DocumentItemId: asOptionalString( getCaseInsensitiveValue( row, 'DocumentItemId' ) ),
        DocumentTypeId: asOptionalString( getCaseInsensitiveValue( row, 'DocumentTypeId' ) ),
        ClientOfferingId: asOptionalString( getCaseInsensitiveValue( row, 'ClientOfferingId' ) ),
        SOWId: asOptionalString( getCaseInsensitiveValue( row, 'SOWId' ) ),
    };
};

const normalizeDocumentItemRow = ( row: unknown ): DocumentItemRow | null => {
    const id = asOptionalString( getCaseInsensitiveValue( row, 'Id' ) );
    if( !id ) return null;

    return { Id: id };
};

const parseArrayPayload = ( response: any ): any[] => {
    if( Array.isArray( response ) ) return response;

    const candidates = [ response?.result, response?.Result, response?.data, response?.Data ];
    for( const candidate of candidates ){
        if( Array.isArray( candidate ) ) return candidate;
        if( typeof candidate === 'string' ){
            try {
                const parsed = JSON.parse( candidate );
                if( Array.isArray( parsed ) ) return parsed;
            } catch {
                // ignore malformed JSON wrappers
            }
        }
    }

    if( typeof response === 'string' ){
        try {
            const parsed = JSON.parse( response );
            if( Array.isArray( parsed ) ) return parsed;
        } catch {
            // ignore malformed JSON payload
        }
    }

    return [];
};



const normalizeClientOutline = (
    payload: any[],
    selectedClientId?: string,
    selectedClientName?: string
): ClientOutline | null => {
    if( payload.length === 0 ) return null;

    const first = payload[ 0 ];
    const wrappedJson = typeof first?.JSON === 'string'
        ? parseArrayPayload( first.JSON )
        : null;
    const clientRows = wrappedJson && wrappedJson.length > 0 ? wrappedJson : payload;

    const selected = selectedClientId
        ? clientRows.find( ( row: any ) =>
            `${ row?.ClientId || '' }`.toLowerCase() === selectedClientId.toLowerCase()
        )
        : undefined;

    const source = selected || clientRows[ 0 ];
    const clientId = `${ source?.ClientId || selectedClientId || '' }`;
    const clientName = `${ source?.ClientName || selectedClientName || 'Client' }`;
    const sowRaw = Array.isArray( source?.SOWs )
        ? source.SOWs
        : typeof source?.SOWs === 'string'
            ? parseArrayPayload( source.SOWs )
            : [];

    const sows: SOWOutline[] = sowRaw.map( ( sow: any ) => {
        const offeringRaw = Array.isArray( sow?.Offerings )
            ? sow.Offerings
            : typeof sow?.Offerings === 'string'
                ? parseArrayPayload( sow.Offerings )
                : [];

        return {
            SOWId: `${ sow?.SOWId || uuid() }`,
            SOWName: `${ sow?.SOWName || 'Untitled SOW' }`,
            DocumentItemId: `${ sow?.DocumentItemId || sow?.documentItemId || '' }`,
            StartDate: `${ sow?.StartDate || sow?.startDate || '' }` || undefined,
            EndDate: `${ sow?.EndDate || sow?.endDate || '' }` || undefined,
            BudgetedHours: asOptionalNumber(
                getCaseInsensitiveValue( sow, 'BudgetedHours' )
            ),
            ExpansionOpportunity: asOptionalBoolean(
                getCaseInsensitiveValue( sow, 'ExpansionOpportunity' )
            ),
            SowLeadConsultantId: asOptionalString(
                getCaseInsensitiveValue( sow, 'SowLeadConsultantId' )
                ?? getCaseInsensitiveValue( sow, 'SOWLeadConsultantId' )
            ),
            SowLeadConsultant: asOptionalString(
                getCaseInsensitiveValue( sow, 'SowLeadConsultant' )
                ?? getCaseInsensitiveValue( sow, 'SOWLeadConsultant' )
            ),
            Offerings: offeringRaw.map( ( offering: any ) => ( {
                OfferingId: `${ offering?.OfferingId || uuid() }`,
                OfferingName: `${ offering?.OfferingName || 'Untitled Offering' }`,
                ClientOfferingId: `${ offering?.ClientOfferingId || uuid() }`,
                PracticeId: asOptionalString( getCaseInsensitiveValue( offering, 'PracticeId' ) ),
            } ) ),
        };
    } );

    return {
        ClientId: clientId,
        ClientName: clientName,
        SOWs: sows,
    };
};

const ClientContent: FC<ClientContentProps> = ( {
    clientId,
    clientName,
    onOpenAddSowDialog,
    onOpenAddOfferingDialog,
} ) => {
    const { executeStoredProcedure, TableSelect, RunInsert, RunUpdate } = useCentralCalls();

    const [ loading, setLoading ]                   = useState( false ),
          [ loadingError, setLoadingError ]         = useState( '' ),
          [ clientOutline, setClientOutline ]       = useState<ClientOutline | null>( null ),
          [ refreshKey, setRefreshKey ]             = useState(0),
          [ msaRow, setMsaRow ]                     = useState<MsaRow | null>( null ),
          [ msaLoading, setMsaLoading ]             = useState( false ),
          [ msaError, setMsaError ]                 = useState( '' ),
          [ isAddingMsa, setIsAddingMsa ]           = useState( false ),
          [ cdaDocumentItem, setCdaDocumentItem ]   = useState<DocumentItemRow | null>( null ),
          [ cdaLoading, setCdaLoading ]             = useState( false ),
          [ cdaError, setCdaError ]                 = useState( '' ),
          [ isAddingCda, setIsAddingCda ]           = useState( false ),
          [ clientCode, setClientCode ]             = useState( '' ),
          [ isEditClientOpen, setIsEditClientOpen ] = useState( false ),
          [ editClientName, setEditClientName ]     = useState( '' ),
          [ editClientCode, setEditClientCode ]     = useState( '' ),
          [ isSavingClient, setIsSavingClient ]     = useState( false ),
          [ editClientError, setEditClientError ]   = useState( '' ),
          [ isDiscoveryOpen, setIsDiscoveryOpen ]   = useState( false );

    const loadMsaRows = async ( selectedClientId: string ) => {
        const response = await TableSelect( MSA_TABLE, { ClientId: selectedClientId } );
        return Array.isArray( response ) ? response : parseArrayPayload( response );
    };

    const loadCdaDocumentItems = async ( selectedClientId: string ) => {
        const response = await TableSelect( DOCUMENT_ITEMS_TABLE, {
            ClientId: selectedClientId,
            DocumentTypeId: CDA_DOCUMENT_TYPE_ID,
        } );
        return Array.isArray( response ) ? response : parseArrayPayload( response );
    };

    const loadClientDetails = async ( selectedClientId: string ) => {
        const response = await TableSelect( 'Clients', { Id: selectedClientId } );
        const rows = Array.isArray( response ) ? response : parseArrayPayload( response );
        if( rows.length === 0 ) return null;

        const row = rows[ 0 ];
        const id = asOptionalString( getCaseInsensitiveValue( row, 'Id' ) );
        if( !id ) return null;

        return {
            Id: id,
            Name: asOptionalString( getCaseInsensitiveValue( row, 'Name' ) ),
            ClientCode: asOptionalString( getCaseInsensitiveValue( row, 'ClientCode' ) ),
        } as ClientRow;
    };

    useEffect( () => {
        if( !clientId ){
            setClientOutline( null );
            setLoading( false );
            setLoadingError( '' );
            setMsaRow( null );
            setMsaLoading( false );
            setMsaError( '' );
            setIsAddingMsa( false );
            setCdaDocumentItem( null );
            setCdaLoading( false );
            setCdaError( '' );
            setIsAddingCda( false );
            return;
        }

        let cancelled = false;

        const loadOutline = async () => {
            setLoading( true );
            setLoadingError( '' );
            setMsaLoading( true );
            setMsaError( '' );
            setCdaLoading( true );
            setCdaError( '' );

            try {
                const outlineResponse = await executeStoredProcedure(
                    'tng.sp_get_ClientPageOutline',
                    { clientId }
                );
                const payload = parseArrayPayload( outlineResponse );
                const normalized = normalizeClientOutline( payload, clientId, clientName );
                console.log('page outlinel..');
                console.log('payload',payload);    
                if( !cancelled ){
                    setClientOutline( normalized );
                }
            } catch( err ){
                if( !cancelled ){
                    const message = err instanceof Error ? err.message : 'Failed to load client page outline.';
                    setLoadingError( message );
                    setClientOutline( null );
                }
            } finally {
                if( !cancelled ){
                    setLoading( false );
                }
            }

            try {
                const details = await loadClientDetails( clientId );
                if( !cancelled ){
                    setClientCode( details?.ClientCode || '' );
                }
            } catch {
                if( !cancelled ){
                    setClientCode( '' );
                }
            }

            try {
                const msaRows = await loadMsaRows( clientId );
                const normalizedMsa = msaRows.length > 0
                    ? normalizeMsaRow( msaRows[ 0 ] )
                    : null;

                if( !cancelled ){
                    setMsaRow( normalizedMsa );
                }
            } catch( err ){
                if( !cancelled ){
                    const message = err instanceof Error ? err.message : 'Failed to load MSA data.';
                    setMsaError( message );
                    setMsaRow( null );
                }
            } finally {
                if( !cancelled ){
                    setMsaLoading( false );
                }
            }

            try {
                const cdaRows = await loadCdaDocumentItems( clientId );
                const normalizedCda = cdaRows.length > 0
                    ? normalizeDocumentItemRow( cdaRows[ 0 ] )
                    : null;

                if( !cancelled ){
                    setCdaDocumentItem( normalizedCda );
                }
            } catch( err ){
                if( !cancelled ){
                    const message = err instanceof Error ? err.message : 'Failed to load CDA data.';
                    setCdaError( message );
                    setCdaDocumentItem( null );
                }
            } finally {
                if( !cancelled ){
                    setCdaLoading( false );
                }
            }
        };

        loadOutline();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ clientId, clientName, refreshKey ] );

    const handleOpenAddSowDialog = () => {
        if( !clientOutline?.ClientId || !onOpenAddSowDialog ) return;
        onOpenAddSowDialog(
            clientOutline.ClientId,
            clientOutline.ClientName,
            () => setRefreshKey( v => v + 1 )
        );
    };

    const handleOpenAddOfferingDialog = ( sowId: string, sowName: string ) => {
        if( !clientOutline?.ClientId || !onOpenAddOfferingDialog ) return;
        onOpenAddOfferingDialog(
            clientOutline.ClientId,
            sowId,
            sowName,
            () => setRefreshKey( v => v + 1 )
        );
    };

    const handleOpenEditSowDialog = ( sow: SOWOutline ) => {
        if( !clientOutline?.ClientId || !onOpenAddSowDialog ) return;
        onOpenAddSowDialog(
            clientOutline.ClientId,
            clientOutline.ClientName,
            () => setRefreshKey( v => v + 1 ),
            {
                sowId: sow.SOWId,
                sowName: sow.SOWName,
                startDate: sow.StartDate,
                endDate: sow.EndDate,
                budgetedHours: sow.BudgetedHours,
                expansionOpportunity: sow.ExpansionOpportunity,
                sowLeadConsultantId: sow.SowLeadConsultantId,
                sowLeadConsultant: sow.SowLeadConsultant,
            }
        );
    };

    const handleAddMsa = async () => {
        if( !clientId || isAddingMsa ) return;

        setIsAddingMsa( true );
        setMsaError( '' );
        try {
            const newDocumentItemId = uuid();

            await RunInsert( DOCUMENT_ITEMS_TABLE, {
                Id: newDocumentItemId,
                ClientId: clientId,
                DocumentTypeId: MSA_DOCUMENT_TYPE_ID,
                Name: 'MSA',
            } );

            await RunInsert( MSA_TABLE, {
                ClientId: clientId,
                DocumentItemId: newDocumentItemId,
            } );
            setRefreshKey( v => v + 1 );
        } catch( err ){
            const message = err instanceof Error ? err.message : 'Failed to add MSA.';
            setMsaError( message );
        } finally {
            setIsAddingMsa( false );
        }
    };

    const handleAddCda = async () => {
        if( !clientId || isAddingCda ) return;

        setIsAddingCda( true );
        setCdaError( '' );
        try {
            const newDocumentItemId = uuid();

            await RunInsert( DOCUMENT_ITEMS_TABLE, {
                Id: newDocumentItemId,
                ClientId: clientId,
                DocumentTypeId: CDA_DOCUMENT_TYPE_ID,
                Name: 'CDA',
            } );

            setRefreshKey( v => v + 1 );
        } catch( err ){
            const message = err instanceof Error ? err.message : 'Failed to add CDA.';
            setCdaError( message );
        } finally {
            setIsAddingCda( false );
        }
    };

    const handleOpenEditClient = () => {
        const resolvedName = ( clientOutline?.ClientName || clientName || '' ).trim();
        setEditClientName( resolvedName );
        setEditClientCode( clientCode );
        setEditClientError( '' );
        setIsEditClientOpen( true );
    };

    const handleSaveClient = async () => {
        if( !clientId || isSavingClient ) return;

        const trimmedName = editClientName.trim();
        if( !trimmedName ){
            setEditClientError( 'Client name is required.' );
            return;
        }

        try {
            setIsSavingClient( true );
            setEditClientError( '' );

            await RunUpdate( 'Clients', clientId, {
                Name: trimmedName,
                ClientCode: editClientCode.trim() || null,
            } );

            setClientCode( editClientCode.trim() );
            setClientOutline( prev => prev ? { ...prev, ClientName: trimmedName } : prev );
            setIsEditClientOpen( false );
            setRefreshKey( v => v + 1 );
        } catch( err ){
            const message = err instanceof Error ? err.message : 'Failed to update client.';
            setEditClientError( message );
        } finally {
            setIsSavingClient( false );
        }
    };

    if( !clientId ){
        return (
            <p className={ 'consultant-widget-empty consultant-dash-empty-state' }>
                Select a client from the side menu.
            </p>
        );
    }

    return (
        <div>
            <Modal
                open={ isEditClientOpen }
                size={ 'tiny' }
                onClose={ () => {
                    if( isSavingClient ) return;
                    setIsEditClientOpen( false );
                } }
            >
                <Modal.Header>Edit Client</Modal.Header>
                <Modal.Content>
                    <Form>
                        { editClientError && (
                            <Message negative icon={ 'warning circle' } content={ editClientError }/>
                        ) }

                        <Form.Input
                            label={ 'Client name' }
                            value={ editClientName }
                            required
                            disabled={ isSavingClient }
                            onChange={ ( _e, data ) => {
                                setEditClientName( String( data.value ?? '' ) );
                                if( editClientError ) setEditClientError( '' );
                            } }
                        />

                        <Form.Input
                            label={ 'Client code' }
                            value={ editClientCode }
                            maxLength={ 4 }
                            disabled={ isSavingClient }
                            onChange={ ( _e, data ) => {
                                setEditClientCode( String( data.value ?? '' ) );
                                if( editClientError ) setEditClientError( '' );
                            } }
                        />
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        type={ 'button' }
                        basic
                        disabled={ isSavingClient }
                        onClick={ () => setIsEditClientOpen( false ) }
                    >
                        Cancel
                    </Button>
                    <Button
                        type={ 'button' }
                        color={ 'purple' }
                        loading={ isSavingClient }
                        disabled={ isSavingClient }
                        onClick={ handleSaveClient }
                    >
                        Save
                    </Button>
                </Modal.Actions>
            </Modal>

            <Modal
                open={ isDiscoveryOpen }
                size={ 'large' }
                closeIcon
                onClose={ () => setIsDiscoveryOpen( false ) }
            >
                <Modal.Header>Client Discovery Workflow</Modal.Header>
                <Modal.Content scrolling>
                    { isDiscoveryOpen && (
                        <DiscoveryWorkflow clientId={ clientId }/>
                    ) }
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        type={ 'button' }
                        basic
                        onClick={ () => setIsDiscoveryOpen( false ) }
                    >
                        Close
                    </Button>
                </Modal.Actions>
            </Modal>

            { loadingError && (
                <Message
                    negative
                    icon={ 'warning circle' }
                    className={ 'document-version-error' }
                    content={ loadingError }
                />
            ) }

            <div className={ 'client-content-header' }>
                <Flexbox
                    align={ FlexAlign.CENTER }
                    justify={ FlexJustify.START }
                    className={ 'client-content-header-title' }
                >
                    <h3 className={ 'client-content-title' }>
                        { clientOutline?.ClientName || clientName || 'Client Page' }
                    </h3>

                    <span className={ 'client-content-header-actions' } role={ 'toolbar' } aria-label={ 'Client actions' }>
                        <Button
                            type={ 'button' }
                            basic
                            compact
                            icon
                            size={ 'mini' }
                            className={ 'client-content-header-action-btn' }
                            aria-label={ 'Edit client' }
                            title={ 'Edit client' }
                            onClick={ handleOpenEditClient }
                        >
                            <Icon name={ 'pencil' } />
                        </Button>
                        <Button
                            type={ 'button' }
                            basic
                            compact
                            icon
                            size={ 'mini' }
                            className={ 'client-content-header-action-btn' }
                            aria-label={ 'Open client discovery workflow' }
                            title={ 'Client discovery workflow' }
                            onClick={ () => setIsDiscoveryOpen( true ) }
                        >
                            <Icon name={ 'user circle outline' } />
                        </Button>
                        <Button
                            type={ 'button' }
                            basic
                            compact
                            icon
                            size={ 'mini' }
                            className={ 'client-content-header-action-btn' }
                            aria-label={ 'Refresh client outline' }
                            title={ 'Refresh' }
                            onClick={ () => setRefreshKey( v => v + 1 ) }
                        >
                            <Icon name={ 'refresh' } />
                        </Button>
                    </span>
                </Flexbox>

                <div className={ 'client-content-doc-tiles' }>
                    <div className={ 'client-content-doc-tile' }>
                        { !cdaLoading && cdaDocumentItem && (
                            <div>
                                <DocumentVersionMini
                                    clientId={ clientOutline?.ClientId || clientId || '' }
                                    LabelName={ 'CDA' }
                                    documentTypeId={ CDA_DOCUMENT_TYPE_ID }
                                    documentItemId={ cdaDocumentItem.Id }
                                />
                            </div>
                        ) }

                        { !cdaLoading && !cdaDocumentItem && (
                            <Button
                                color={ 'purple' }
                                content={ isAddingCda ? 'Adding CDA...' : 'Add CDA' }
                                loading={ isAddingCda }
                                disabled={ isAddingCda || !clientId }
                                onClick={ handleAddCda }
                            />
                        ) }
                    </div>

                    <div className={ 'client-content-doc-tile' }>
                        { !msaLoading && msaRow && (
                            <div>
                                <DocumentVersionMini
                                    clientId={ clientOutline?.ClientId || clientId || '' }
                                    LabelName={ 'MSA' }
                                    documentTypeId={ MSA_DOCUMENT_TYPE_ID }
                                    documentItemId={ msaRow.DocumentItemId || '' }
                                />
                            </div>
                        ) }

                        { !msaLoading && !msaRow && (
                            <Button
                                color={ 'purple' }
                                content={ isAddingMsa ? 'Adding MSA...' : 'Add MSA' }
                                loading={ isAddingMsa }
                                disabled={ isAddingMsa || !clientId }
                                onClick={ handleAddMsa }
                            />
                        ) }
                    </div>
                </div>
            </div>

            { msaError && (
                <Message
                    negative
                    icon={ 'warning circle' }
                    className={ 'document-version-error' }
                    content={ msaError }
                />
            ) }

            { cdaError && (
                <Message
                    negative
                    icon={ 'warning circle' }
                    className={ 'document-version-error' }
                    content={ cdaError }
                />
            ) }

            { loading && (
                <div className={ 'document-version-empty' }>
                    <Icon name={ 'spinner' } loading /> Loading client outline...
                </div>
            ) }

            { !loading && clientOutline && (
                <SOWTabMenu
                    clientId={ clientOutline.ClientId }
                    sows={ clientOutline.SOWs }
                    onOpenAddSowDialog={ handleOpenAddSowDialog }
                    onOpenEditSowDialog={ handleOpenEditSowDialog }
                    onOpenAddOfferingDialog={ handleOpenAddOfferingDialog }
                />
            ) }

            { !loading && !clientOutline && !loadingError && (
                <div className={ 'document-version-empty' }>No SOWs found for this client.</div>
            ) }
        </div>
    );
};

export default ClientContent;
